import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search } from "lucide-react";
import { useState, useMemo } from "react";
import CommandCard from "./hero-commands/CommandCard";
import { useHeroCommands, useUserHeroCommands } from "@/hooks/useHeroCommands";
import { ICommand, UserHeroCommand } from "@/types/heroCommands";
import InfoModal from "@/components/ui/InfoModal";
import { useVideoBySlug } from "@/hooks/useVideos";
import VideoLink from "@/components/ui/VideoLink";

export default function HeroCommands() {
  const { data: commands = [], isLoading: commandsLoading, error: commandsError } = useHeroCommands();
  const { data: userCommands = [], isLoading: userCommandsLoading } = useUserHeroCommands();
  return (
    <div className="space-y-6 p-4 md:p-6">
      <HeroCommandsSearch 
        commands={commands} 
        userCommands={userCommands}
        commandsLoading={commandsLoading || userCommandsLoading} 
        commandsError={commandsError} 
      />
    </div>
  );
}

const HeroCommandsSearch = ({
  commands, 
  userCommands,
  commandsLoading, 
  commandsError, 
}: {commands: ICommand[], userCommands: UserHeroCommand[], commandsLoading: boolean, commandsError: Error | null}) => {
  const heroCommandsOverviewVideo = useVideoBySlug('hero-commands-overview');

  const [search, setSearch] = useState("");
  const [type, setType] = useState("All");
  const filteredFeatures = useMemo(() => {
    let filtered = commands;

    if (search.trim()) {
      const searchTerm = search.toLowerCase().trim();
      filtered = filtered.filter(feature =>
        feature.title.toLowerCase().includes(searchTerm) ||
        feature.category.toLowerCase().includes(searchTerm) ||
        feature.description.toLowerCase().includes(searchTerm)
      );
    }
    if (type !== "All") {
      filtered = filtered.filter(feature =>
        feature.category === type
      );
    }
    return filtered;
  }, [search, type, commands]);

  const subtitleOptions = useMemo(() => ["All", ...new Set(commands.map(feature => feature.category))], [commands]);

  const isLoading = commandsLoading;
  const hasError = commandsError;   
  if (isLoading) return <div className="flex justify-center items-center h-[calc(100vh-200px)]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div></div>
  if (hasError && !commands.length) return <div className="text-center py-8 text-red-500">Error loading commands: {hasError?.message}. Please try refreshing the page.</div>;

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-gray">Hero Commands™ Library</h1>
        <InfoModal 
          title="Hero Commands Overview" 
          description={
            <div className="space-y-4">
              <p className="text-sm text-gray-700 mb-4">
                Hero Commands are pre-built skills and custom instructions tailored for your AI Employee. Each command is designed to handle specific business scenarios.
              </p>
              
              {heroCommandsOverviewVideo &&   <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={heroCommandsOverviewVideo || "https://www.loom.com/embed/4f1fdb3a5c224ed6bbeb9324bb6df782?sid=72995a38-329d-44e4-b2a8-8fb727cf4c81"}
                  frameBorder="0"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                />
              </div>}
              {heroCommandsOverviewVideo && <VideoLink to='hero-commands-overview' />}

              <div className="space-y-3">
                <div>
                  <h4 className="font-medium mb-1">How Hero Commands Work:</h4>
                  <ul className="text-sm space-y-1 text-gray-600">
                    <li>• <strong>Pre-built Skills:</strong> Custom instructions crafted from real business experience</li>
                    <li>• <strong>Simple Setup:</strong> Just fill out a form with your specific details</li>
                    <li>• <strong>Easy Activation:</strong> Toggle commands on/off as needed</li>
                    <li>• <strong>Two Types:</strong> Situational (like testimonial collection) and Generic (like objection handling)</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium mb-1">Using Hero Commands:</h4>
                  <ul className="text-sm space-y-1 text-gray-600">
                    <li>• <strong>Situational:</strong> Activate via Zapier or CSV upload with specific call purpose</li>
                    <li>• <strong>Generic:</strong> AI automatically uses based on conversation flow</li>
                    <li>• <strong>Testing:</strong> Use "Test Scenarios" to preview command behavior</li>
                    <li>• <strong>Integration:</strong> Active commands appear in Business Profile settings</li>
                  </ul>
                </div>
              </div>
            </div>
          }
          triggerClassName="ml-2"
        />
      </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="relative md:max-w-[350px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-gray pointer-events-none" size={18} />
            <Input
              placeholder="Search by title, category, or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="md:max-w-[350px] w-full md:ml-auto">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue placeholder="Filter by type" /></SelectTrigger>
              <SelectContent>
                {subtitleOptions.map(subtitle => (<SelectItem key={subtitle} value={subtitle}>{subtitle}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredFeatures.map((feature) => (
          <CommandCard key={feature.id} userCommand={userCommands.find(command => command.hero_commands?.id === feature.id)} feature={feature}  />
        ))}
      </div>

      {filteredFeatures.length === 0 && !isLoading && (
        <div className="text-center py-12 text-gray-500">
          <p className="text-xl mb-2">🤔</p>
          <p className="font-semibold">No commands found matching your filters.</p>
          <p className="text-sm">Try adjusting your search or filter criteria.</p>
        </div>
      )}
    </>
  );
};